/**
 * Interactive UI guides — named, code-reviewed walkthroughs that Scout can
 * offer ("Do you want me to show you?") and the tour engine plays.
 *
 * Shared between server and UI on purpose: the server (Scout's
 * offer_ui_guide tool) validates guide ids + role gates so the model can
 * only ever offer walkthroughs that exist and that the user may perform;
 * the UI resolves the same id to steps and plays them. Because guides are
 * code, they change in the same PR as the screens they walk through —
 * a ui test asserts every anchor referenced here exists in the source.
 *
 * Step routes may contain ":tenantId", substituted by the tour engine.
 * Steps without an anchor render as a centered card (no spotlight).
 */
export const GUIDE_ROLE_LEVEL = {
    standard_user: 0,
    agent_admin: 1,
    tenant_admin: 2,
};
export const UI_GUIDES = [
    {
        id: "platform-basics",
        title: "Platform basics",
        requiredRole: "standard_user",
        summary: "The first-login tour: agents, channels, chats, composer, files, Scout.",
        steps: [
            {
                route: "/tenants/:tenantId/workspace?mode=chat",
                anchor: "[data-tour='agent-picker']",
                title: "Your agents",
                body: "Every agent you can talk to, grouped by what matters right now: agents that need you first, agents working on something second, the rest tucked under “more agents”. Click a name to switch to it.",
            },
            {
                anchor: "[data-tour='channels']",
                title: "Channels",
                body: "Channels are shared spaces for a topic or team: everyone in the channel sees the conversation. Chats outside a channel stay private to you.",
            },
            {
                anchor: "[data-tour='chats']",
                title: "Recent chats",
                body: "Your conversations with the selected agent. Each chat keeps its own context, so use New chat when you switch topics.",
            },
            {
                anchor: "[data-tour='composer']",
                title: "Talk to your agent",
                body: "Type here and press Enter to send. Type @ to bring another agent into the conversation or to reference a workspace file, and / to run a skill.",
            },
            {
                anchor: "[data-tour='attach']",
                interactive: true,
                title: "Share files",
                body: "The + button attaches files: documents and images (up to 5 per message) your agent can read and work with. Click it to try.",
            },
            {
                anchor: "[data-tour='composer-tools']",
                interactive: true,
                title: "Choose tools",
                body: "The tools button controls which tools this agent can use. Click it to open the list.",
            },
            {
                anchor: "[data-tour='tools-popup']",
                noMask: true,
                title: "Tools available here",
                body: "This popup shows the tools available to the agent. Toggle a whole set on or off, or expand one to pick individual tools. Click Next.",
            },
            {
                anchor: "[data-tour='composer-skill']",
                interactive: true,
                title: "Run a skill",
                body: "The / button lists skill shortcuts you can run in a message. Click it to open the list.",
            },
            {
                anchor: "[data-tour='skill-popup']",
                noMask: true,
                title: "Skill shortcuts",
                body: "This popup shows the skill shortcuts available to this agent. Pick one to drop it into your message. Click Next.",
            },
            {
                anchor: "[data-tour='workspace-trigger']",
                interactive: true,
                title: "Open the workspace",
                body: "This opens your agent's workspace files and outputs in a panel on the right. Click it to open the panel.",
            },
            {
                anchor: "[data-tour='workspace-panel']",
                noMask: true,
                title: "Files and outputs",
                body: "Your agent's files, generated artifacts and browser view open here on the right. Click Next to carry on.",
            },
            {
                anchor: "[data-tour='scout']",
                interactive: true,
                event: "proclaw:close-artifact",
                title: "Meet Scout",
                body: "Stuck, or something isn't behaving? Scout explains how the platform works, checks agent and workflow health, and can raise a support request. Click it to open Scout.",
            },
        ],
    },
    {
        id: "create-first-agent",
        title: "Create your first agent",
        requiredRole: "tenant_admin",
        summary: "Create and deploy your first AI agent from a template.",
        steps: [
            {
                route: "/tenants/:tenantId/workspace?mode=chat",
                anchor: "[data-tour='profile-menu']",
                interactive: true,
                title: "Open your profile menu",
                body: "Click your profile at the bottom of the left sidebar. Everything you manage for the workspace lives behind here.",
            },
            {
                anchor: "[data-tour='menu-tenant-settings']",
                interactive: true,
                title: "Open Tenant settings",
                body: "Choose Tenant settings to open the workspace management dialog.",
            },
            {
                anchor: "[data-tour='settings-nav-agents']",
                interactive: true,
                title: "Go to Agents",
                body: "Open the Agents section. This lists your agents and the New Agent button.",
            },
            {
                anchor: "[data-tour='agent-new']",
                interactive: true,
                title: "Start a new agent",
                body: "Click New Agent to see the ways you can add one.",
            },
            {
                anchor: "[data-tour='agent-deploy-instance']",
                interactive: true,
                title: "Deploy a new instance",
                body: "Choose Deploy New Instance. The setup wizard opens right here in the dialog, so you never leave this screen.",
            },
            {
                anchor: "[data-tour='agent-templates']",
                interactive: true,
                title: "Pick a template",
                body: "Every agent starts from a template that sets sensible defaults for its role, runtime and tools. Click the one closest to the job.",
            },
            {
                anchor: "[data-tour='wizard-continue']",
                interactive: true,
                title: "Continue",
                body: "Click Continue to move on to naming and configuring your agent.",
            },
            {
                title: "You take it from here",
                body: "Step through identity, model and infrastructure, then Deploy when you're happy.",
            },
        ],
    },
    {
        id: "change-agent-model",
        title: "Change an agent's AI model",
        requiredRole: "agent_admin",
        summary: "Switch an agent's model, set a backup, then sync it.",
        requiresAgent: true,
        steps: [
            {
                route: "/tenants/:tenantId/workspace?mode=chat",
                anchor: "[data-tour='agent-settings']",
                interactive: true,
                title: "Open agent settings",
                body: "With an agent selected, click the settings icon at the top right, next to the notifications bell, to open its settings.",
            },
            {
                anchor: "[data-tour='model-config']",
                noMask: true,
                title: "Change the model",
                body: "This is the Model box. Pick a new primary model here, and optionally a backup model used if the primary is unavailable. Then click Update & Sync to apply it to the agent. Click Next when you're done.",
            },
        ],
    },
    {
        id: "add-mcp-server",
        title: "Add an MCP server to an agent",
        requiredRole: "tenant_admin",
        summary: "Register or enable an MCP tool for the workspace, then give it to an agent.",
        requiresAgent: true,
        steps: [
            {
                route: "/tenants/:tenantId/workspace?mode=chat",
                anchor: "[data-tour='profile-menu']",
                interactive: true,
                title: "Open your profile menu",
                body: "Click your profile at the bottom of the left sidebar to reach the workspace settings.",
            },
            {
                anchor: "[data-tour='menu-tenant-settings']",
                interactive: true,
                title: "Open Tenant settings",
                body: "Choose Tenant settings to open the workspace management dialog.",
            },
            {
                anchor: "[data-tour='tenant-nav-mcp']",
                interactive: true,
                title: "Go to MCP Servers",
                body: "Open the MCP Servers section. It lists every MCP tool your workspace can use: the ones your provider published and your own.",
            },
            {
                anchor: "[data-tour='mcp-create']",
                interactive: true,
                title: "Create an MCP server",
                body: "Click Create MCP to register your own server. (Or click Enable on a published server to switch it on without creating one.)",
            },
            {
                anchor: "[data-tour='mcp-editor']",
                noMask: true,
                title: "Fill it in, then save or cancel",
                body: "Enter the name, transport, URL and authentication (any secrets are kept in your company's private vault), then click Create MCP Server to save, or Cancel to back out.",
            },
        ],
    },
    {
        id: "create-workflow",
        title: "Build a workflow",
        requiredRole: "tenant_admin",
        summary: "Open the Workflows view, start one, and build it on the canvas.",
        requiresAgent: true,
        steps: [
            {
                route: "/tenants/:tenantId/workspace?mode=workflows",
                anchor: "[data-tour='workflow-create']",
                interactive: true,
                title: "Start a workflow",
                body: "This is your Workflows list. Click New Workflow to begin one.",
            },
            {
                anchor: "[data-tour='workflow-create-confirm']",
                interactive: true,
                noMask: true,
                title: "Name it and create",
                body: "Type a name for your workflow in the field above (for example \"Lead triage\"), then click Create. Your new workflow opens on the visual canvas.",
            },
            {
                anchor: "[data-tour='node-palette']",
                interactive: true,
                dragToAdvance: true,
                noMask: true,
                title: "Drag a node onto the canvas",
                body: "Drag a node from this palette onto the canvas: a trigger to start, then agent steps, conditions and actions. Drag one over now to continue.",
            },
            {
                anchor: "[data-tour='config-panel']",
                noMask: true,
                title: "Configure the node",
                body: "The node you dropped is selected, and its configuration opens here on the right. Set what it does and connect it onward, then click Next.",
            },
            {
                anchor: "[data-tour='workflow-save']",
                noMask: true,
                title: "Save your workflow",
                body: "When the node is set up, click Save to keep your workflow. Click Next when you're done.",
            },
            {
                route: "/tenants/:tenantId/workspace?mode=chat",
                anchor: "[data-tour='composer']",
                title: "Or build it from chat",
                body: "You don't have to build it by hand. You can also describe the workflow to an agent here in chat, and it drafts one for you to open and refine on the canvas.",
            },
        ],
    },
    {
        id: "raise-support-ticket",
        title: "Raise a support request",
        requiredRole: "standard_user",
        summary: "Get a problem to the human platform team and follow its progress.",
        steps: [
            {
                anchor: "[data-tour='scout']",
                title: "Start with Scout",
                body: "Describe the problem to Scout first — it can often diagnose agents and workflows on the spot. If it can't fix it, it will offer to raise a support request for you.",
            },
            {
                title: "Follow your request",
                body: "Your requests live in Scout's Support view (the life-buoy icon in its header). You'll be notified in the platform — and on Teams or email if you chose that — whenever the platform team replies or the status changes.",
            },
        ],
    },
];
export function getUiGuide(id) {
    return UI_GUIDES.find((g) => g.id === id);
}
//# sourceMappingURL=ui-guides.js.map